import Button from 'assets/components/button/Button';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import DataMap from 'assets/components/dataMap/DataMap';
import Icon, { iconStyles } from 'assets/components/icon/Icon';
import { inputStyles } from 'assets/components/inputs/Input';
import TextInput from 'assets/components/inputs/text/TextInput';
import { getLocales, Language } from 'assets/locales/Locale';
import { buildOrSearchRegex } from 'assets/utils/data/Regex';
import Theme from 'assets/themes/Theme.Common';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import { sortBy } from 'lodash';
import { getLossCodeCategoryNames } from 'module/claims/helper/LossCode.Category';
import { CSSProperties, useMemo, useState } from 'react';

type SelectableLossCode = Utils.FormData<Model.ILossCode>;

type Props = {
  lossCodes: SelectableLossCode[];
  value?: string;
  disabled?: boolean;
  error?: any;
  width?: CSSProperties['width'];
  onChange(data: { lossCode: string }): void;
};

const uncategorizedKey = '';

export default function LossCodeSelect({ lossCodes, value, disabled, error, width, onChange }: Props) {
  const { lang, currentLanguage } = getLocales();
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState<string | undefined>(undefined);
  // Built without the global flag so .test() stays stateless across items
  const searchRegex = buildOrSearchRegex(search?.trim(), 'i');

  const openCategory = (categoryName: string | undefined) => {
    setCategory(categoryName);
    setSearch(undefined);
  };

  const describe = (lossCode: SelectableLossCode) =>
    (currentLanguage === Language.french ? lossCode.descFr : lossCode.descnEn) || lossCode.descnEn;

  const grouped = useMemo(() => {
    const groups: Record<string, SelectableLossCode[]> = {};
    lossCodes?.forEach((lossCode) => {
      const names = getLossCodeCategoryNames(lossCode);
      (names.length ? names : [uncategorizedKey]).forEach((name) => (groups[name] ??= []).push(lossCode));
    });
    return groups;
  }, [lossCodes, currentLanguage]);
  const categories = useMemo(() => sortBy(Object.keys(grouped)), [grouped]);

  const visibleCategories = searchRegex
    ? categories.filter((it) => searchRegex.test(it || lang.noCategory))
    : categories;
  const visibleLossCodes = sortBy(
    category === undefined
      ? []
      : grouped[category]?.filter((it) => !searchRegex || searchRegex.test(`${it.code} ${describe(it)}`)),
    (it) => describe(it)?.toLowerCase()
  );

  const selected = useMemo(() => lossCodes?.find((it) => it.code === value), [lossCodes, value]);
  const buttonBody = selected ? describe(selected) : lang.select;

  return (
    <span
      style={{
        display: width ? 'block' : 'inline-block',
        width,
        borderBottom: error ? `2px solid ${Theme.Color.error}` : undefined,
      }}
      title={error || buttonBody}
    >
      <DropdownButton
        buttonBody={buttonBody}
        buttonMedia={<Icon class="fas-list" />}
        horizontalPosition="auto_fixed"
        verticalPosition="auto_fixed"
        subcomponents={
          width
            ? {
                ...ThemeDropdownButtonPopup,
                button: {
                  ...ThemeDropdownButtonPopup.button,
                  style: {
                    width: '100%',
                    display: 'block',
                    clickableArea: { justifyContent: 'flex-start' },
                    buttonText: {
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  },
                },
              }
            : ThemeDropdownButtonPopup
        }
        disabled={disabled || !lossCodes?.length}
        tabIndex={null}
        onAnimateOut={() => openCategory(undefined)}
        htmlElementProps={{ title: buttonBody }}
        dropdownActions={
          <TextInput
            key={category ?? 'categories'}
            name="search"
            placeholder={`${lang.search}...`}
            value={search}
            onChange={({ search }) => setSearch(search)}
            class={inputStyles.variations.search}
            icon={<Icon class={iconStyles.common.search} />}
            autoComplete={false}
          />
        }
        dropdownOptions={({ animateOut }) =>
          category === undefined ? (
            <DataMap
              data={visibleCategories}
              render={({ data: categoryName }) => (
                <Button
                  key={categoryName || uncategorizedKey}
                  onClick={() => openCategory(categoryName)}
                  render={categoryName || lang.noCategory}
                  media={<Icon class="fas-folder" />}
                  class={ThemeButtonMenuItem}
                  htmlElementProps={{ title: categoryName || lang.noCategory }}
                />
              )}
            />
          ) : (
            <>
              <Button
                onClick={() => openCategory(undefined)}
                render={lang.back}
                media={<Icon class="fas-arrow-left" />}
                class={ThemeButtonMenuItem}
                htmlElementProps={{ title: lang.back }}
              />
              <DataMap
                data={visibleLossCodes}
                render={({ data: lossCode }) => (
                  <Button
                    key={lossCode.code}
                    onClick={() => {
                      onChange({ lossCode: lossCode.code });
                      openCategory(undefined);
                      animateOut();
                    }}
                    render={describe(lossCode)}
                    class={ThemeButtonMenuItem}
                    active={lossCode.code === value}
                    htmlElementProps={{ title: describe(lossCode) }}
                  />
                )}
              />
            </>
          )
        }
      />
    </span>
  );
}
